import React from 'react';

import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import {
  desktop,
  mobile,
  ProductListingFilter as BaseProductListingFilter,
  ProductListingFilterStyle,
} from '@techstyle/react-components';

import { usePageNum } from '../GridPageNumProvider/GridPageNumProvider';

const ChevronUp = styled(FaChevronUp)`
  color: ${({ theme }) => theme.colors.active};

  ${mobile`
    color: ${props => props.theme.colors.lavender350};
  `}
`;

const ChevronDown = styled(FaChevronDown)`
  color: ${({ theme }) => theme.colors.active};

  ${mobile`
    color: ${props => props.theme.colors.lavender350};
  `}
`;

const circleIconStyle = css`
  ${desktop`
      height: 32px;
      width: 32px;
      margin-left: 4px;
    `};

  ${mobile`
      height: ${({ theme }) => theme.spacing.large}px;
      width: ${({ theme }) => theme.spacing.large}px;
    `};
`;

const filterFieldButtonStyle = css`
  &&& {
    padding: 0;
  }

  ${desktop`
      &&& {
        padding: ${({ theme }) => theme.spacing.moderate}px 0;
        span {
          color: #000;
        }
      }
    `}

  text-transform: uppercase;
`;

const filterFieldLabelStyle = css`
  font-size: 16px;
  letter-spacing: ${0.3 / 16}em;
  color: ${props => props.theme.colors.default};

  ${mobile`font-size: 16px;`};
`;

const filterCountStyle = css`
  font-size: 16px;
  letter-spacing: ${0.3 / 16}em;
  color: ${({ theme }) => theme.colors.default};

  ${mobile`font-size: 16px;`};
`;

const nestedHeadingStyle = css`
  font-size: 10px;
  font-weight: 500;
  letter-spacing: ${0.83 / 10}em;
  color: ${({ theme }) => theme.colors.subdued};
`;

const itemStyle = css`
  ${desktop`padding: ${props => props.theme.spacing.tiny}px 0;`};
`;

const swatchGridItemStyle = css`
  margin-right: 0;
  display: flex;
  justify-content: center;
  flex-direction: column;

  ${desktop`
    height: 66px;
    width: 81px;
  `};

  ${mobile`
    height: 74px;
    width: 85px;
    margin-right: auto;

    &:hover ${BaseProductListingFilter.CircleIcon} {
      ${({ isChecked, disabled, colorSwatches, colorValue }) =>
        !isChecked &&
        !disabled &&
        `
          border: none;
          box-shadow: ${
            colorSwatches && colorSwatches[colorValue] === colorSwatches.White
              ? 'inset 0 0 0 1px #333'
              : 'unset'
          } ;
      `};
    }
  `};
`;

const swatchGridLabelStyle = css`
  font-weight: 400;
  line-height: 14.4px;
  letter-spacing: 0.3px;
  text-align: center;
  color: #000;

  ${desktop`
      font-size: 12px;
    `};

  ${mobile`
    font-size: 14px;
  `};
`;

const tagsGridLabelStyle = css`
  font-weight: 400;
  line-height: 14.4px;
  letter-spacing: 0.3px;
  text-align: center;
  color: #000;

  ${desktop`
    font-size: 12px;
  `};

  ${mobile`
    font-size: 14px;
  `};

  border-color: ${({ theme }) => theme.colors.stone} !important;
  &:hover {
    border-color: ${({ theme }) => theme.colors.black900} !important;
  }
`;

const listStyle = css`
  padding-left: 0;
  padding-bottom: ${({ theme }) => theme.spacing.moderate}px;
  padding-top: 0;
  padding-right: 0;

  ${mobile`
    padding-bottom: ${({ theme }) => theme.spacing.small}px;
  `}
`;

const swatchGridListStyle = css`
  padding-left: 0;
  padding-bottom: ${({ theme }) => theme.spacing.moderate}px;
  padding-top: 0;
  padding-right: 0;

  ${mobile`
    padding-bottom: ${({ theme }) => theme.spacing.small}px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(85px, 1fr));
  `}
`;

const itemLabelStyle = css`
  font-size: 14px;
  font-weight: ${({ isChecked }) => (isChecked ? 'bold' : 'normal')};
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.default};
`;

const ListingFilter = styled(BaseProductListingFilter)`
  background-color: ${props => props.theme.colors.white};
  box-shadow: inset 0 1px 0 0 ${({ theme }) => theme.colors.lavender250};

  :last-child {
    box-shadow: inset 0 -1px 0 0 ${({ theme }) => theme.colors.lavender250},
      inset 0 1px 0 0 ${({ theme }) => theme.colors.lavender250};
  }

  background-color: ${props => props.theme.colors.white};

  :last-child {
    box-shadow: inset 0 1px 0 0 ${({ theme }) => theme.colors.lavender250};
  }

  h2 {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 1.75px;
    padding-bottom: 16px;
  }
`;

const inputStyle = css`
  border-radius: 3px;
  border: solid 1px ${({ theme }) => theme.colors.default};
  background-color: ${({ theme }) => theme.colors.white};
  margin-right: ${({ theme }) => theme.spacing.tiny}px;

  ${mobile`
    width: 22px;
    height: 22px;
  `};
`;

const inputCheckedStyle = css`
  background-color: ${({ theme }) => theme.colors.default};
`;

// Empty to override white label defaults
const checkmarkCheckedStyle = css``;
const checkmarkStyle = css``;

const checkboxProps = {
  inputStyle,
  inputCheckedStyle,
  checkmarkStyle,
  checkmarkCheckedStyle,
};

const gridInputStyle = css`
  width: 48px;
  height: 48px;

  border-radius: 28px;
  background-color: ${({ theme }) => theme.colors.gray150};
  color: ${({ theme }) => theme.colors.default};
`;

const gridInputCheckedStyle = css`
  border: solid 2px ${({ theme }) => theme.colors.active};
  background-color: ${({ theme }) => theme.colors.active};
  color: ${({ theme }) => theme.colors.white};
`;

const gridCheckboxProps = {
  inputStyle,
  gridInputStyle,
  inputCheckedStyle: gridInputCheckedStyle,
};

function ProductListingFilter(props) {
  const { setPageNum } = usePageNum();

  const onChange = async e => {
    if (props.onChange) {
      // Waits for `setPageNum` to update the URL with the new page number
      // before calling `props.onChange` to update the URL to reflect the filter
      // change, otherwise the page number update could be lost due to a
      // race condition.
      await setPageNum(1);
      props.onChange(e);

      // this gets called when a filter is clicked inside a menu
      const filter = e.filterObject;
      if (filter?.addFilterCallback && e.event.target.checked) {
        await filter.addFilterCallback();
      } else if (filter?.removeFilterCallback) {
        await filter.removeFilterCallback();
      }
    }
  };

  return (
    <ListingFilter
      filterStyle={
        props?.filterField?.field === 'color'
          ? ProductListingFilterStyle.SWATCH_GRID
          : ProductListingFilterStyle.TAGS_GRID
      }
      circleIconStyle={circleIconStyle}
      filterFieldButtonStyle={filterFieldButtonStyle}
      filterFieldLabelStyle={filterFieldLabelStyle}
      collapseIconComponent={ChevronUp}
      expandIconComponent={ChevronDown}
      filterCountStyle={filterCountStyle}
      gridCheckboxProps={gridCheckboxProps}
      nestedHeadingStyle={nestedHeadingStyle}
      itemStyle={itemStyle}
      listStyle={listStyle}
      swatchGridListStyle={swatchGridListStyle}
      tagsGridListStyle={listStyle}
      swatchGridItemStyle={swatchGridItemStyle}
      swatchGridLabelStyle={swatchGridLabelStyle}
      tagsGridLabelStyle={tagsGridLabelStyle}
      itemLabelStyle={itemLabelStyle}
      checkboxProps={checkboxProps}
      {...props}
      onChange={onChange}
    />
  );
}

export default ProductListingFilter;
